/**
 * Credential scrubbing for anything that carries a URL into analytics.
 *
 * Auth puts live, single-use credentials in the URL: `token_hash` on a recovery
 * link, `code` on every PKCE callback, and — in Supabase's implicit flow —
 * `access_token`/`refresh_token` in the FRAGMENT. Captured, they sit in an
 * events table that far more people can read than can read the auth schema.
 *
 * ⚠️ This lives here, and not inside `instrumentation-client.ts`, because there
 * are TWO paths into PostHog and a browser `before_send` only covers one of
 * them. `onRequestError` receives `request.path` WITH the query string attached
 * (Next's own docs: "resource path, e.g. /blog?name=foo"), so a server-side
 * render failure on `/callback?code=…` would ship a live PKCE code from the
 * server, where no browser hook runs. One scrubber, both runtimes, one list.
 *
 * ⚠️ The fragment matters and was previously missed. `URL.searchParams` does not
 * see `#access_token=…`, and posthog-js only stops capturing hashes at
 * `defaults >= '2026-06-25'` — this project sets no `defaults`, so
 * `disable_capture_url_hashes` resolves false and hashes ARE captured. The two
 * params most worth redacting were listed and not redacted. They are now.
 *
 * `/update-password` also strips its own token from the URL on mount. This is
 * the backstop, and unlike that one it covers `/callback` too — and it runs
 * before hydration, which is the window the page's own strip cannot close.
 *
 * Zero imports, on purpose: this module is reachable from the browser bundle,
 * the node server AND the edge runtime.
 */

export const SENSITIVE_PARAMS = [
  'token_hash',
  'code',
  'access_token',
  'refresh_token',
] as const

const REDACTED = 'redacted'

/**
 * Redact in place. Returns whether anything changed, so callers can leave the
 * original string untouched when there was nothing to scrub.
 *
 * One loop, two callers — the query string and the fragment need identical
 * treatment, and writing it twice is how the fragment came to be forgotten in
 * the first place.
 */
function scrub(params: URLSearchParams): boolean {
  let touched = false
  for (const key of SENSITIVE_PARAMS) {
    if (params.has(key)) {
      params.set(key, REDACTED)
      touched = true
    }
  }
  return touched
}

/**
 * Redact credentials from an absolute URL, a relative path, or a bare path.
 *
 * Non-strings pass straight through — `before_send` hands us `unknown`, and a
 * scrubber that throws on an unexpected property type would take the whole
 * event pipeline down with it.
 */
export function redactUrl<T>(value: T): T {
  if (typeof value !== 'string' || !value) return value

  // A relative path has no base, so `new URL(value)` throws. Give it one and
  // strip it back off — cheaper and far more predictable than hand-parsing.
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(value)
  let url: URL
  try {
    url = new URL(value, isAbsolute ? undefined : 'http://x.invalid')
  } catch {
    return value
  }

  let touched = scrub(url.searchParams)

  // The fragment, which `url.searchParams` cannot see. `url.hash` includes the
  // leading '#'; Supabase's implicit flow returns access_token/refresh_token here.
  const hash = url.hash.slice(1)
  if (hash) {
    const fragment = new URLSearchParams(hash)
    if (scrub(fragment)) {
      url.hash = `#${fragment.toString()}`
      touched = true
    }
  }

  if (!touched) return value
  const out = isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
  return out as unknown as T
}
